/**
 * Food Prep label helpers — compact thermal-label renderer (≈50 × 40 mm),
 * VBE1 token build/decode, and a printer-agnostic system-print path that
 * works on iOS (AirPrint), Android and desktop.
 */
import QRCode from "qrcode";

/** Build the opaque VBE1 token for a food_prep_item id. */
export function buildFpToken(id) {
  const json = JSON.stringify({ t: "fp", id });
  const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `VBE1:${b64}`;
}

/** Decode any VBE1 token → { t, id } | null. */
export function decodeToken(text) {
  if (!text || !text.startsWith("VBE1:")) return null;
  let raw = text.slice(5).replace(/-/g, "+").replace(/_/g, "/");
  raw += "=".repeat((4 - (raw.length % 4)) % 4);
  try {
    const obj = JSON.parse(atob(raw));
    if (obj && obj.t && obj.id) return obj;
  } catch {
    return null;
  }
  return null;
}

/** Live verdict for an item, computed client-side: ok | expiring | expired. */
export function foodState(item, now = Date.now()) {
  const ub = item?.use_by ? Date.parse(item.use_by) : NaN;
  const amber = Number.isFinite(item?.amber_window_minutes) ? item.amber_window_minutes : 120;
  if (!Number.isFinite(ub)) return { state: "ok", minutes_remaining: null };
  const mins = Math.floor((ub - now) / 60000);
  let state = "ok";
  if (mins <= 0) state = "expired";
  else if (mins <= amber) state = "expiring";
  return { state, minutes_remaining: mins };
}

/** Human-friendly "2d 3h", "45m", "overdue 12m". */
export function fmtRemaining(mins) {
  if (mins == null) return "";
  const overdue = mins < 0;
  let m = Math.abs(mins);
  const d = Math.floor(m / 1440); m -= d * 1440;
  const h = Math.floor(m / 60); m -= h * 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (!d && (m || !h)) parts.push(`${m}m`);
  const txt = parts.slice(0, 2).join(" ");
  return overdue ? `overdue ${txt}` : txt;
}

/** Format an ISO datetime in the given IANA timezone (falls back to local). */
export function fmtDateTime(iso, tz) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz && tz !== "UTC" ? tz : undefined,
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

const MM_PER_IN = 25.4;
const DEFAULT_DIMS = { widthMm: 50, heightMm: 40, dpi: 203 };
const QR_MIN_MM = 15; // Minimum QR size = 1.5 cm × 1.5 cm (scannability requirement).
const FONT = "'Helvetica Neue', Arial, sans-serif";

/** Render a compact, human-readable food label onto `canvas`, scaled to the
 *  platform-configured physical dimensions. The QR code is never smaller than
 *  1.5 cm; body text stays in a left column kept clear of the QR. */
export async function renderFoodLabel(canvas, { name, useByText, prepText, byText, siteText, qtyText, token }, dims = {}) {
  const { widthMm, heightMm, dpi } = { ...DEFAULT_DIMS, ...dims };
  const W = Math.round((widthMm * dpi) / MM_PER_IN);
  const H = Math.round((heightMm * dpi) / MM_PER_IN);
  const pad = Math.round(Math.min(W, H) * 0.06);
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#000000";

  // QR — bottom-right, minimum 1.5 cm, clamped so it always fits the label.
  const qrFloor = Math.round((QR_MIN_MM * dpi) / MM_PER_IN);
  let qr = Math.max(Math.round(H * 0.4), qrFloor);
  qr = Math.min(qr, H - pad * 2, Math.round(W * 0.55));
  const qrX = W - pad - qr;
  const qrY = H - pad - qr;
  const colW = Math.max(40, qrX - pad * 2); // left text column, clear of the QR

  // Company branding — top-right, two lines.
  const brandSize = Math.round(H * 0.055);
  ctx.textAlign = "right";
  ctx.font = `bold ${brandSize}px ${FONT}`;
  const brandW = Math.max(ctx.measureText("Vanguard").width, ctx.measureText("Beacon").width);
  ctx.fillText("Vanguard", W - pad, pad + brandSize);
  ctx.fillText("Beacon", W - pad, pad + brandSize * 2 + 2);
  ctx.textAlign = "left";

  // Food name — up to 2 lines across the top (auto-shrunk to fit).
  const nameMaxW = W - pad * 2 - brandW - 8;
  let size = Math.round(H * 0.14);
  const minSize = Math.round(H * 0.1);
  ctx.font = `bold ${size}px ${FONT}`;
  let lines = wrapText(ctx, (name || "").toUpperCase(), nameMaxW, 2);
  while (size > minSize && lines.length > 2) {
    size -= 2;
    ctx.font = `bold ${size}px ${FONT}`;
    lines = wrapText(ctx, (name || "").toUpperCase(), nameMaxW, 2);
  }
  let y = pad + size;
  lines.slice(0, 2).forEach((ln) => { ctx.fillText(ln, pad, y); y += size + 4; });

  // USE BY block — boxed, kept inside the left column so it never hits the QR.
  const boxY = Math.round(H * 0.4);
  const boxH = Math.round(H * 0.26);
  ctx.fillStyle = "#000000";
  ctx.fillRect(pad, boxY, colW, boxH);
  ctx.fillStyle = "#ffffff";
  const lbl = Math.round(H * 0.06);
  ctx.font = `600 ${lbl}px ${FONT}`;
  ctx.fillText("USE BY", pad + 8, boxY + lbl + 5);
  let val = Math.round(H * 0.13);
  const valMin = Math.round(H * 0.08);
  ctx.font = `bold ${val}px ${FONT}`;
  while (val > valMin && ctx.measureText(useByText || "—").width > colW - 16) {
    val -= 1;
    ctx.font = `bold ${val}px ${FONT}`;
  }
  ctx.fillText(ellipsize(ctx, useByText || "—", colW - 12), pad + 8, boxY + boxH - 8);

  // Footer — qty / prep / by / site in the left column, below the box.
  ctx.fillStyle = "#222222";
  const foot = Math.round(H * 0.06);
  const lh = foot + 3;
  const footTop = boxY + boxH + 4;
  const maxLines = Math.max(1, Math.floor((H - pad - footTop) / lh));
  const rows = [];
  if (qtyText) rows.push({ t: qtyText, bold: true });
  if (prepText) rows.push({ t: prepText });
  if (byText) rows.push({ t: byText });
  if (siteText) rows.push({ t: siteText });
  let fy = footTop + foot;
  rows.slice(0, maxLines).forEach((r) => {
    ctx.font = `${r.bold ? "bold" : "500"} ${foot + (r.bold ? 1 : 0)}px ${FONT}`;
    ctx.fillText(ellipsize(ctx, r.t, colW), pad, fy);
    fy += lh;
  });

  // QR — bottom-right.
  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, token, {
    errorCorrectionLevel: "M", margin: 1, width: qr,
    color: { dark: "#000000", light: "#ffffff" },
  });
  ctx.drawImage(qrCanvas, qrX, qrY, qr, qr);
}

/** Open a print window sized to the thermal label and auto-print `copies` copies. */
export function printFoodLabel(canvas, title = "Food label", dims = {}, copies = 1) {
  const { widthMm, heightMm } = { ...DEFAULT_DIMS, ...dims };
  const n = Math.max(1, Math.min(parseInt(copies, 10) || 1, 500));
  const dataUrl = canvas.toDataURL("image/png");
  const w = window.open("", "_blank", "width=420,height=380");
  if (!w) return;
  const doc = w.document;
  doc.open();
  doc.write("<!doctype html><html><head></head><body></body></html>");
  doc.close();
  doc.title = title;
  const style = doc.createElement("style");
  style.textContent = `
    @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
    html, body { margin: 0; padding: 0; }
    img.lbl { width: ${widthMm}mm; height: ${heightMm}mm; display: block; image-rendering: pixelated; page-break-after: always; break-after: page; }
    img.lbl:last-child { page-break-after: auto; break-after: auto; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;
  doc.head.appendChild(style);
  let pending = n;
  const maybePrint = () => { if (--pending <= 0) { try { w.print(); } catch (e) { console.debug(e); } } };
  for (let i = 0; i < n; i++) {
    const img = doc.createElement("img");
    img.className = "lbl";
    img.src = dataUrl;
    doc.body.appendChild(img);
    if (img.complete) maybePrint(); else { img.onload = maybePrint; img.onerror = maybePrint; }
  }
}

function wrapText(ctx, text, maxWidth, maxLines) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const word of words) {
    const test = cur ? `${cur} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = word;
      if (lines.length === maxLines) break;
    } else {
      cur = test;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  return lines.length ? lines : [text || ""];
}

function ellipsize(ctx, text, maxWidth) {
  if (!text) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

